import { LitElement, css, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { EVENT, makeCustomEvent } from '../../util/events';
import { choose } from 'lit/directives/choose.js';
import './steps';
import { consume, provide } from '@lit/context';
import { formDataContext } from '../../contexts/formData';
import { initRateQuoteFormData, RateQuoteFormData } from '../../util/form';
import { submitInputs } from '../../controllers/inputs';
import { agentContext } from '../../contexts/agent';
import { Producer } from '../../util/api';
import { SOURCE } from '../../util/source';
import { sourceContext } from '../../contexts/source';
import { getHostURL } from '../../util/analytics';

const INPUT_STEP = {
    START: 1,
    MEMBER: 2,
    ASSISTANCE: 3,
    // ESTIMATE: 4,
};

@customElement('rate-quote-form')
export class RateQuoteForm extends LitElement {
    static styles = css`
        :host {
            padding-top: 40px;
            padding-bottom: 40px;
            display: flex;
            justify-content: center;
            align-items: center;
        }
        .wrapper {
            display: flex;
            flex-direction: column;
            gap: 16px;
            width: 326px;
            max-width: 90%;
        }
        .tracker {
            display: flex;
            flex-direction: column;
            gap: 32px;
        }
    `;

    constructor() {
        super();
        this.manualStepHandler = this.manualStepHandler.bind(this);
    }

    connectedCallback() {
        super.connectedCallback();
        this.addEventListener(EVENT.NEXT_STEP, this.nextStepHandler);
        this.addEventListener(EVENT.PREVIOUS_STEP, this.previousStepHandler);
        this.addEventListener(EVENT.UPDATE_FORM, this.updateFormHandler as EventListener);
        this.addEventListener(EVENT.SUBMIT, this.submitHandler);
        window.addEventListener(EVENT.MANUAL_STEP, this.manualStepHandler as EventListener);
        // window.location.hash = `#step-${this.step.current}`;
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this.removeEventListener(EVENT.NEXT_STEP, this.nextStepHandler);
        this.removeEventListener(EVENT.PREVIOUS_STEP, this.previousStepHandler);
        this.removeEventListener(EVENT.UPDATE_FORM, this.updateFormHandler as EventListener);
        this.removeEventListener(EVENT.SUBMIT, this.submitHandler);
        window.removeEventListener(EVENT.MANUAL_STEP, this.manualStepHandler as EventListener);
    }

    protected firstUpdated(): void {
        const hashParams = new URLSearchParams(window.location.hash.split('?')[1] || '');
        const urlParams = new URLSearchParams(window.location.search);
        const pnumber = hashParams.get('pnumber') || urlParams.get('pnumber') || undefined;
        if (this.submittedForm) {
            this.formData = this.submittedForm;
        }
        if (this.agent || pnumber) {
            this.formData = { ...this.formData, producer: this.agent, pnumber };
        }
        if (this.initSubsidyStep) {
            this.step = { ...this.step, current: INPUT_STEP.ASSISTANCE };
        }
    }

    @state()
    private step: { current: number; total: number } = { current: 1, total: Object.entries(INPUT_STEP).length };

    @state()
    submitting: boolean = false;

    @state()
    toast: {
        show: boolean;
        message?: string;
    } = { show: false };

    @provide({ context: formDataContext })
    @state()
    formData: RateQuoteFormData = initRateQuoteFormData();

    @property({ attribute: false })
    submittedForm!: RateQuoteFormData;

    @property({ type: Boolean })
    initSubsidyStep = false;

    @consume({ context: agentContext })
    agent?: Producer;

    @consume({ context: sourceContext, subscribe: true })
    source!: SOURCE;

    updated(changedProperties: Map<string | number | symbol, unknown>): void {
        super.updated(changedProperties);
        if (changedProperties.has('step')) {
            this.dispatchEvent(makeCustomEvent(EVENT.SCROLL_TO_TOP));
            window.bcncDataLayer = window.bcncDataLayer || {};
            window.bcncDataLayer.page = window.bcncDataLayer.page || {};
            window.bcncDataLayer.page.view_url = `${getHostURL()}/step-${this.step.current}`;
        }
    }

    private nextStepHandler() {
        if (this.step.current >= this.step.total) return;
        const newCurrent = this.step.current + 1;
        this.step = { ...this.step, current: newCurrent };
    }

    private previousStepHandler() {
        if (this.step.current === 0) return;
        const newCurrent = this.step.current - 1;
        this.step = { ...this.step, current: newCurrent };
    }

    private manualStepHandler(e: CustomEvent) {
        if (!e.detail.step || this.step.current === e.detail.step) return;
        this.step = { ...this.step, current: e.detail.step };
    }

    private updateFormHandler(e: CustomEvent) {
        if (!e.detail.data) return;
        this.formData = e.detail.data;
    }

    private createSubmittedFormSnapshot(): RateQuoteFormData {
        return {
            ...this.formData,
            members: this.formData.members.map((member) => ({ ...member })),
            contactInfo: this.formData.contactInfo ? { ...this.formData.contactInfo } : undefined,
            producer: this.formData.producer ? { ...this.formData.producer } : undefined,
        };
    }

    private async submitHandler() {
        this.submitting = true;
        try {
            const submittedForm = this.createSubmittedFormSnapshot();
            const data = await submitInputs(this.formData);
            if (data === null) {
                this.toast = { show: true, message: 'null data returned' };
                setTimeout(() => {
                    this.toast = { show: false };
                }, 6250);
            } else {
                this.dispatchEvent(
                    makeCustomEvent(EVENT.UPDATE_RESPONSE, {
                        data,
                        severity: this.formData.severity,
                        formData: submittedForm,
                    })
                );
            }
        } catch (e: unknown) {
            console.error(e);
            this.toast = { show: true, message: (e as Error)?.message };
            setTimeout(() => {
                this.toast = { show: false };
            }, 6250);
        } finally {
            this.submitting = false;
        }
    }

    private errorHeader = html`<h3>Error finding form step</h3>`;

    get errorToast() {
        if (!this.toast.show) return nothing;
        return html`<lh-toast
            variant="error"
            shade="color"
            header="Error submitting form"
            description=${this.toast.message || nothing}
            position="top"
            has-icon
            open
        ></lh-toast>`;
    }

    get renderStep() {
        return choose(
            this.step.current,
            [
                [INPUT_STEP.START, () => html`<rq-step-start></rq-step-start>`],
                [INPUT_STEP.MEMBER, () => html`<rq-step-members></rq-step-members>`],
                [INPUT_STEP.ASSISTANCE, () => html`<rq-step-assistance></rq-step-assistance>`],
                // [INPUT_STEP.ESTIMATE, () => html`<rq-step-estimate></rq-step-estimate>`],
            ],
            () => this.errorHeader
        );
    }

    get loadingMask() {
        if (!this.submitting) return nothing;
        return html`
            <div
                style="
                position: fixed;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                background-color: rgba(128, 128, 128, 0.5);
                display: flex;
                justify-content: center;
                align-items: center;
                z-index: 1000;
            "
            >
                <lh-loader></lh-loader>
            </div>
        `;
    }

    render() {
        return html`
            <div class="wrapper">
                <div class="tracker">
                    <lh-progress-tracker
                        active-step=${this.step.current}
                        steps=${this.step.total}
                    ></lh-progress-tracker>
                    <lh-typography alignment="left" type="label"
                        >Step ${this.step.current} of ${this.step.total}</lh-typography
                    >
                </div>
                ${this.renderStep}
            </div>
            ${this.errorToast} ${this.loadingMask}
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'rate-quote-form': RateQuoteForm;
    }
}
